import React, {useEffect, useState} from "react";
import {Divider, Grid, Typography} from "@mui/material";
import {inventoryCorrectionReasons} from "../constants/securspace-constants";
import {CorrectionDisplayContainer, CorrectionHeaderCol} from "../../views/inventory-correction/styles";
import OriginalTypography from "./OriginalTypography";
import RevisedTypography from "./RevisedTypography";
import OriginalGridItem from "./OriginalGridItem";
import RevisedGridItem from "./RevisedGridItem";

const fieldTypes = {DATE: "DATE", ASSET: "ASSET", DRIVER: "DRIVER"}

const CorrectionComparison = ({inventoryCorrection, getDates}) => {
  const {
    locationInventoryActivityGetVO,
    reason,
    driverFirstName,
    driverLastName,
    driverLicenseNumber
  } = inventoryCorrection;

  const [revisedDateFields, setRevisedDateFields] = useState({
    checkInDate: getDates().revisedCheckIn,
    checkOutDate: getDates().revisedCheckOut
  });
  const [revisedAssetFields, setRevisedAssetFields] = useState({
    containerNumber: inventoryCorrection?.containerNumber,
    chassisNumber: inventoryCorrection?.chassisNumber,
    truckLicensePlateNumber: inventoryCorrection?.truckLicensePlateNumber,
    trailerNumber: inventoryCorrection?.trailerNumber,
    scacCode: inventoryCorrection?.scacCode
  });

  const [revisedDriverFields, setRevisedDriverFields] = useState({
    firstName: inventoryCorrection?.driverFirstName,
    lastName: inventoryCorrection?.driverLastName,
    licenseNumber: inventoryCorrection?.driverLicenseNumber,
  });

  const isSingleColumn = reason !== inventoryCorrectionReasons.ASSET_DOES_NOT_BELONG_TO_BUYER;

  const showRevisedField = (fieldType) => {
    if (reason === inventoryCorrectionReasons.ASSET_DOES_NOT_BELONG_TO_BUYER)
      return false;

    switch (fieldType) {
      case fieldTypes.DATE:
        return reason === inventoryCorrectionReasons.ASSET_CHECKED_OUT_ON_WRONG_DATE || reason === inventoryCorrectionReasons.ASSET_LEFT_THE_YARD;

      case fieldTypes.ASSET:
        return reason === inventoryCorrectionReasons.ASSET_NUMBER_ENTERED_INCORRECTLY;

      case fieldTypes.DRIVER:
        return Boolean(driverFirstName) || Boolean(driverLastName) || Boolean(driverLicenseNumber);

    }
  }

  const nullSafeField = (item) => {
    return item ? item : "-"
  }

  const isRevisedField = (field1, field2, type) => {
    return (showRevisedField(type) && nullSafeField(field1) !== nullSafeField(field2));
  }

  useEffect(() => {

    if (reason === inventoryCorrectionReasons.ASSET_CHECKED_OUT_ON_WRONG_DATE || reason === inventoryCorrectionReasons.ASSET_LEFT_THE_YARD) {
      setRevisedAssetFields({
        containerNumber: inventoryCorrection?.containerNumber,
        chassisNumber: inventoryCorrection?.chassisNumber,
        truckLicensePlateNumber: inventoryCorrection?.truckLicensePlateNumber,
        trailerNumber: inventoryCorrection?.trailerNumber,
        scacCode: inventoryCorrection?.scacCode
      })
    } else if (reason === inventoryCorrectionReasons.ASSET_NUMBER_ENTERED_INCORRECTLY) {
      setRevisedDateFields({
        checkInDate: getDates().originalCheckIn,
        checkOutDate: getDates().originalCheckOut
      })
    }

    if ((!driverFirstName && !driverLastName && !driverLicenseNumber)) {
      setRevisedDriverFields({
        firstName: inventoryCorrection?.driverFirstName,
        lastName: inventoryCorrection?.driverLastName,
        licenseNumber: inventoryCorrection?.driverLicenseNumber,
      })
    }

  }, [driverFirstName, driverLastName, driverLicenseNumber, getDates, inventoryCorrection, reason])

  return <Grid container item rowSpacing={1}>
    <Grid container item>
      <CorrectionHeaderCol item>
        <CorrectionDisplayContainer container item rowSpacing={1} direction="column">

          <Divider/>
          <Grid item>
            <Typography variant="body1">Date</Typography>
          </Grid>
          <Divider/>

          <Grid item>
            <Typography variant="subtitle2">Check In Date</Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle2">Check Out Date</Typography>
          </Grid>
        </CorrectionDisplayContainer>
      </CorrectionHeaderCol>
      <OriginalGridItem isSingleColumn={isSingleColumn}>
        <CorrectionDisplayContainer container item rowSpacing={1} direction="column">

          <Divider/>
          <Grid item>
            <Typography variant="body1">Original</Typography>
          </Grid>
          <Divider/>

          <Grid item>
            <OriginalTypography
              isRevised={isRevisedField(getDates().originalCheckIn, getDates().revisedCheckIn, fieldTypes.DATE)}>
              {nullSafeField(getDates().originalCheckIn)}
            </OriginalTypography>
          </Grid>

          <Grid item>
            <OriginalTypography
              isRevised={isRevisedField(getDates().originalCheckOut, getDates().revisedCheckIn, fieldTypes.DATE)}>
              {nullSafeField(getDates().originalCheckOut)}
            </OriginalTypography>
          </Grid>
        </CorrectionDisplayContainer>
      </OriginalGridItem>
      <RevisedGridItem isSingleColumn={isSingleColumn}>
        <CorrectionDisplayContainer container item rowSpacing={1} direction="column">

          <Divider/>
          <Grid item>
            <Typography variant="body1">Buyer's Revision</Typography>
          </Grid>
          <Divider/>

          <Grid item>
            <RevisedTypography
              isRevised={isRevisedField(getDates().originalCheckIn, getDates().revisedCheckIn, fieldTypes.DATE)}>
              {nullSafeField(revisedDateFields.checkInDate)}
            </RevisedTypography>
          </Grid>

          <Grid item>
            <RevisedTypography
              isRevised={isRevisedField(getDates().originalCheckOut, getDates().revisedCheckOut, fieldTypes.DATE)}>
              {nullSafeField(revisedDateFields.checkOutDate)}
            </RevisedTypography>
          </Grid>
        </CorrectionDisplayContainer>
      </RevisedGridItem>
    </Grid>
    <Grid container item>
      <CorrectionHeaderCol item>
        <CorrectionDisplayContainer container item rowSpacing={1} direction="column">

          <Divider/>
          <Grid item>
            <Typography variant="body1">Asset Number</Typography>
          </Grid>
          <Divider/>

          <Grid item>
            <Typography variant="subtitle2">Container</Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle2">Chassis</Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle2">Trailer</Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle2">Truck License</Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle2">SCAC Code</Typography>
          </Grid>
        </CorrectionDisplayContainer>
      </CorrectionHeaderCol>
      <OriginalGridItem isSingleColumn={isSingleColumn}>
        <CorrectionDisplayContainer container item rowSpacing={1} direction="column">

          <Divider/>
          <Grid item>
            <Typography variant="body1">Original</Typography>
          </Grid>
          <Divider/>

          <Grid item>
            <OriginalTypography
              isRevised={isRevisedField(locationInventoryActivityGetVO?.containerNumber, inventoryCorrection?.containerNumber, fieldTypes.ASSET)}>
              {nullSafeField(locationInventoryActivityGetVO?.containerNumber)}
            </OriginalTypography>
          </Grid>
          <Grid item>
            <OriginalTypography
              isRevised={isRevisedField(locationInventoryActivityGetVO?.chassisNumber, inventoryCorrection?.chassisNumber, fieldTypes.ASSET)}>
              {nullSafeField(locationInventoryActivityGetVO?.chassisNumber)}
            </OriginalTypography>
          </Grid>
          <Grid item>
            <OriginalTypography
              isRevised={isRevisedField(locationInventoryActivityGetVO?.trailerNumber, inventoryCorrection?.trailerNumber, fieldTypes.ASSET)}>
              {nullSafeField(locationInventoryActivityGetVO?.trailerNumber)}
            </OriginalTypography>
          </Grid>
          <Grid item>
            <OriginalTypography
              isRevised={isRevisedField(locationInventoryActivityGetVO?.truckLicensePlateNumber, inventoryCorrection?.truckLicensePlateNumber, fieldTypes.ASSET)}>
              {nullSafeField(locationInventoryActivityGetVO?.truckLicensePlateNumber)}
            </OriginalTypography>
          </Grid>
          <Grid item>
            <OriginalTypography
              isRevised={isRevisedField(locationInventoryActivityGetVO?.scacCode, inventoryCorrection?.scacCode, fieldTypes.ASSET)}>
              {nullSafeField(locationInventoryActivityGetVO?.scacCode)}
            </OriginalTypography>
          </Grid>
        </CorrectionDisplayContainer>
      </OriginalGridItem>
      <RevisedGridItem isSingleColumn={isSingleColumn}>
        <CorrectionDisplayContainer container item rowSpacing={1} direction="column">

          <Divider/>
          <Grid item>
            <Typography variant="body1">Buyer's Revision</Typography>
          </Grid>
          <Divider/>

          <Grid item>
            <RevisedTypography
              isRevised={isRevisedField(locationInventoryActivityGetVO?.containerNumber, inventoryCorrection?.containerNumber, fieldTypes.ASSET)}>
              {nullSafeField(revisedAssetFields.containerNumber)}
            </RevisedTypography>
          </Grid>
          <Grid item>
            <RevisedTypography
              isRevised={isRevisedField(locationInventoryActivityGetVO?.chassisNumber, inventoryCorrection?.chassisNumber, fieldTypes.ASSET)}>
              {nullSafeField(revisedAssetFields.chassisNumber)}
            </RevisedTypography>
          </Grid>
          <Grid item>
            <RevisedTypography
              isRevised={isRevisedField(locationInventoryActivityGetVO?.trailerNumber, inventoryCorrection?.trailerNumber, fieldTypes.ASSET)}>
              {nullSafeField(revisedAssetFields.trailerNumber)}
            </RevisedTypography>
          </Grid>
          <Grid item>
            <RevisedTypography
              isRevised={isRevisedField(locationInventoryActivityGetVO?.truckLicensePlateNumber, inventoryCorrection?.truckLicensePlateNumber, fieldTypes.ASSET)}>
              {nullSafeField(revisedAssetFields.truckLicensePlateNumber)}
            </RevisedTypography>
          </Grid>
          <Grid item>
            <RevisedTypography
              isRevised={isRevisedField(locationInventoryActivityGetVO?.scacCode, inventoryCorrection?.scacCode, fieldTypes.ASSET)}>
              {nullSafeField(revisedAssetFields.scacCode)}
            </RevisedTypography>
          </Grid>
        </CorrectionDisplayContainer>
      </RevisedGridItem>
    </Grid>

    <Grid container item>
      <CorrectionHeaderCol item>
        <CorrectionDisplayContainer container item rowSpacing={1} direction="column">

          <Divider/>
          <Grid item>
            <Typography variant="body1">Driver</Typography>
          </Grid>
          <Divider/>

          <Grid item>
            <Typography variant="subtitle2">First Name</Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle2">Last Name</Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle2">License Number</Typography>
          </Grid>
        </CorrectionDisplayContainer>
      </CorrectionHeaderCol>
      <OriginalGridItem isSingleColumn={isSingleColumn}>
        <CorrectionDisplayContainer container item rowSpacing={1} direction="column">

          <Divider/>
          <Grid item>
            <Typography variant="body1">Original</Typography>
          </Grid>
          <Divider/>

          <Grid item>
            <OriginalTypography
              isRevised={isRevisedField(locationInventoryActivityGetVO?.driverFirstName, inventoryCorrection?.driverFirstName, fieldTypes.DRIVER)}>
              {nullSafeField(locationInventoryActivityGetVO?.driverFirstName)}
            </OriginalTypography>
          </Grid>
          <Grid item>
            <OriginalTypography
              isRevised={isRevisedField(locationInventoryActivityGetVO?.driverLastName, inventoryCorrection?.driverLastName, fieldTypes.DRIVER)}>
              {nullSafeField(locationInventoryActivityGetVO?.driverLastName)}
            </OriginalTypography>
          </Grid>
          <Grid item>
            <OriginalTypography
              isRevised={isRevisedField(locationInventoryActivityGetVO?.driverLicenseNumber, inventoryCorrection?.driverLicenseNumber, fieldTypes.DRIVER)}>
              {nullSafeField(locationInventoryActivityGetVO?.driverLicenseNumber)}
            </OriginalTypography>
          </Grid>
        </CorrectionDisplayContainer>
      </OriginalGridItem>
      <RevisedGridItem isSingleColumn={isSingleColumn}>
        <CorrectionDisplayContainer container item rowSpacing={1} direction="column">

          <Divider/>
          <Grid item>
            <Typography variant="body1">Buyer's Revision</Typography>
          </Grid>
          <Divider/>

          <Grid item>
            <RevisedTypography
              isRevised={isRevisedField(locationInventoryActivityGetVO?.driverFirstName, inventoryCorrection?.driverFirstName, fieldTypes.DRIVER)}>
              {nullSafeField(revisedDriverFields.firstName)}
            </RevisedTypography>
          </Grid>
          <Grid item>
            <RevisedTypography
              isRevised={isRevisedField(locationInventoryActivityGetVO?.driverLastName, inventoryCorrection?.driverLastName, fieldTypes.DRIVER)}>
              {nullSafeField(revisedDriverFields.lastName)}
            </RevisedTypography>
          </Grid>
          <Grid item>
            <RevisedTypography
              isRevised={isRevisedField(locationInventoryActivityGetVO?.driverLicenseNumber, inventoryCorrection?.driverLicenseNumber, fieldTypes.DRIVER)}>
              {nullSafeField(revisedDriverFields.licenseNumber)}
            </RevisedTypography>
          </Grid>
        </CorrectionDisplayContainer>
      </RevisedGridItem>
    </Grid>
  </Grid>
}

export default CorrectionComparison;
